import React, { useState } from 'react';
import { X, Plus, RefreshCw, Download, Trash2, Edit, Archive } from 'lucide-react';
import type { LegalFile, Payment } from '../../types';
import Base64Image from '../../components/ui/Base64Image';
import AddAttachmentModal from './AddAttachmentModal';
import AddPaymentModal from './AddPaymentModal';

interface FileDetailsModalProps {
  selectedFile: LegalFile;
  activeTab: 'overview' | 'payments' | 'events' | 'attachments';
  payments: any[];
  events: any[];
  attachments: any[];
  loadingEntities: boolean;
  language: 'en' | 'ar';
  strings: any;
  getTypeLabel: (typeID: string) => string;
  getFileTypeName: (typeID: string, language: 'en' | 'ar') => string;
  getFileStatusName: (statusID: string, language: 'en' | 'ar') => string;
  getCourtTypeName: (courtTypeID: string, language: 'en' | 'ar') => string;
  getCaseTypeName: (caseTypeID: string, language: 'en' | 'ar') => string;
  getAttachmentTypeName: (type: string, language: 'en' | 'ar') => string;
  getAttachmentTypeIcon: (type: string) => string;
  getMimeTypeFromAttachmentType: (type: string) => string;
  getPaymentTypeLabel: (paymentTypeId: string) => string;
  formatDate: (timestamp: number) => string;
  onClose: () => void;
  onTabChange: (tab: 'overview' | 'payments' | 'events' | 'attachments') => void;
  onEditFile: () => void;
  onArchiveFile: () => void;
  onViewAttachment: (attachment: any) => void;
  onDownloadAttachment: (attachment: any) => void;
  onAddAttachment: () => void;
  onUploadAttachment: (attachmentData: any) => Promise<void>;
  onAddPayment: (paymentData: Payment) => void;
  onDeletePayment: (paymentId: string) => Promise<void>;
  onDeleteAttachment: (attachmentId: string) => Promise<void>;
  user: any;
  selectedRole: any;
  // Inline editing props
  isEditing: boolean;
  editData: Partial<LegalFile>;
  setEditData: (data: Partial<LegalFile> | ((prev: Partial<LegalFile>) => Partial<LegalFile>)) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  presetData: any;
}

const FileDetailsModal: React.FC<FileDetailsModalProps> = ({
  selectedFile,
  activeTab,
  payments,
  events,
  attachments,
  loadingEntities,
  language,
  strings,
  getTypeLabel,
  getFileTypeName,
  getFileStatusName,
  getCourtTypeName,
  getCaseTypeName,
  getAttachmentTypeName,
  getAttachmentTypeIcon,
  getMimeTypeFromAttachmentType,
  getPaymentTypeLabel,
  formatDate,
  onClose,
  onTabChange,
  onEditFile,
  onArchiveFile,
  onViewAttachment,
  onDownloadAttachment,
  onAddAttachment,
  onUploadAttachment,
  onAddPayment,
  onDeletePayment,
  onDeleteAttachment,
  user,
  selectedRole,
  // Inline editing props
  isEditing,
  editData,
  setEditData,
  onSaveEdit,
  onCancelEdit,
  presetData
}) => {
  // Helper function to determine if payment is income or expense
  const isPaymentIncome = (payment: any) => {
    return payment.isIncome === true;
  };

  // Helper function to get file icon based on format
  const getFileIconFromFormat = (format: string): string => {
    const formatLower = format?.toLowerCase() || '';
    
    // Images
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(formatLower)) {
      return '🖼️';
    }
    
    // Documents
    if (['pdf'].includes(formatLower)) {
      return '📄';
    }
    if (['doc', 'docx'].includes(formatLower)) {
      return '📝';
    }
    
    // Spreadsheets
    if (['xls', 'xlsx', 'csv'].includes(formatLower)) {
      return '📊';
    }
    
    // Presentations
    if (['ppt', 'pptx'].includes(formatLower)) {
      return '📽️';
    }
    
    // Text files
    if (['txt', 'rtf'].includes(formatLower)) {
      return '📄';
    }
    
    // Default
    return '📎';
  };

  // Helper function to check if file is an image
  const isImageFile = (attachment: any): boolean => {
    const format = attachment.format?.toLowerCase() || '';
    return ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(format);
  };

  // Helper functions for inline editing
  const handleInputChange = (field: keyof LegalFile, value: string) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDateChange = (field: keyof LegalFile, value: string) => {
    const timestamp = value ? new Date(value).getTime().toString() : '';
    setEditData(prev => ({
      ...prev,
      [field]: timestamp
    }));
  };

  const [showAddAttachmentModal, setShowAddAttachmentModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);

  const handleAddAttachment = () => {
    setShowAddAttachmentModal(true);
  };

  const handleCloseAddAttachment = () => {
    setShowAddAttachmentModal(false);
  };

  const handleAddPayment = () => {
    setShowAddPaymentModal(true);
  };

  const handleCloseAddPayment = () => {
    setShowAddPaymentModal(false);
  };

  const handlePaymentAdded = (paymentData: Payment) => {
    onAddPayment(paymentData);
    setShowAddPaymentModal(false);
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذه الدفعة؟' : 'Are you sure you want to delete this payment?')) {
      try {
        await onDeletePayment(paymentId);
      } catch (error) {
        console.error('Error deleting payment:', error);
        // You could add a toast notification here if you have one
      }
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا المرفق؟' : 'Are you sure you want to delete this attachment?')) {
      try {
        await onDeleteAttachment(attachmentId);
      } catch (error) {
        console.error('Error deleting attachment:', error);
        // You could add a toast notification here if you have one
      }
    }
  };

  const handleRefreshPayments = () => {
    // This will trigger a refresh of the payments data
    // The parent component should handle the actual refresh logic
    console.log('Refreshing payments...');
    // You can add a callback prop for refresh if needed
  };

  const handleExportCSV = () => {
    try {
      // Prepare CSV data
      const csvData = payments.map(payment => ({
        'Payment ID': payment.id || 'N/A',
        'Description': payment.description || 'N/A',
        'Amount': payment.value || 0,
        'Currency': payment.currency || 'EGP',
        'Type': isPaymentIncome(payment) ? 'Income' : 'Expense',
        'Payment Method': payment.paymentMethod || 'N/A',
        'Status': payment.status || 'N/A',
        'Payment Date': payment.paymentDate ? new Date(Number(payment.paymentDate)).toLocaleDateString() : 'N/A',
        'Due Date': payment.dueDate ? new Date(Number(payment.dueDate)).toLocaleDateString() : 'N/A',
        'Client Name': payment.clientName || 'N/A',
        'Invoice Number': payment.invoiceNumber || 'N/A',
        'Receipt Number': payment.receiptNumber || 'N/A',
        'Notes': payment.notes || 'N/A',
        'Created Date': formatDate(Number(payment.createdAt))
      }));

      // Convert to CSV format
      const headers = Object.keys(csvData[0] || {});
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => headers.map(header => `"${(row as any)[header] || ''}"`).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `payments_${selectedFile.title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log('CSV exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center text-white font-semibold text-lg">
                📄
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                  {selectedFile.title}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {selectedFile.caseNumber} • {getFileTypeName(selectedFile.typeID || '', language)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={onEditFile}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors duration-200 shadow-lg hover:shadow-xl"
                title={language === 'ar' ? 'تعديل الملف' : 'Edit File'}
              >
                <Edit className="w-4 h-4" />
                <span className="font-medium">{language === 'ar' ? 'تعديل' : 'Edit'}</span>
              </button>
              <button
                onClick={onArchiveFile}
                className="flex items-center space-x-2 px-4 py-2 bg-accent text-primary rounded-lg hover:bg-accent-light transition-colors duration-200 shadow-lg hover:shadow-xl"
                title={language === 'ar' ? 'أرشفة الملف' : 'Archive File'}
              >
                <Archive className="w-4 h-4" />
                <span className="font-medium">{language === 'ar' ? 'أرشفة' : 'Archive'}</span>
              </button>
            <button
              onClick={onClose}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex space-x-1 bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
            {[
              { id: 'overview', label: language === 'ar' ? 'نظرة عامة' : 'Overview' },
              { id: 'payments', label: language === 'ar' ? 'المدفوعات' : 'Payments' },
              { id: 'events', label: language === 'ar' ? 'الأحداث' : 'Events' },
              { id: 'attachments', label: language === 'ar' ? 'المرفقات' : 'Attachments' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id as any)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white dark:bg-slate-600 text-primary dark:text-primary-light shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {isEditing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {language === 'ar' ? 'العنوان' : 'Title'}
                    </label>
                      <input
                        type="text"
                        value={editData.title || ''}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                      />
                  </div>
                  <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {language === 'ar' ? 'رقم القضية' : 'Case Number'}
                    </label>
                      <input
                        type="text"
                        value={editData.caseNumber || ''}
                        onChange={(e) => handleInputChange('caseNumber', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {language === 'ar' ? 'الوصف' : 'Description'}
                    </label>
                    <textarea
                      value={editData.description || ''}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white resize-none"
                    />
                  </div>

                  <div className="flex items-center justify-end space-x-3">
                    <button
                      onClick={onCancelEdit}
                      className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                    >
                      {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </button>
                    <button
                      onClick={onSaveEdit}
                      className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      {language === 'ar' ? 'حفظ' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
                        {language === 'ar' ? 'معلومات الملف' : 'File Information'}
                </h3>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                            {language === 'ar' ? 'العنوان' : 'Title'}:
                          </span>
                          <p className="text-slate-800 dark:text-white">{selectedFile.title}</p>
                        </div>
                  <div>
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                            {language === 'ar' ? 'رقم القضية' : 'Case Number'}:
                          </span>
                          <p className="text-slate-800 dark:text-white">{selectedFile.caseNumber}</p>
                  </div>
                  <div>
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                            {language === 'ar' ? 'النوع' : 'Type'}:
                          </span>
                          <p className="text-slate-800 dark:text-white">{getFileTypeName(selectedFile.typeID || '', language)}</p>
                  </div>
                  <div>
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                            {language === 'ar' ? 'الحالة' : 'Status'}:
                          </span>
                          <p className="text-slate-800 dark:text-white">{getFileStatusName(selectedFile.statusID || '', language)}</p>
                        </div>
                  </div>
                </div>
              </div>

                <div className="space-y-4">
                  <div>
                      <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
                        {language === 'ar' ? 'التفاصيل' : 'Details'}
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                            {language === 'ar' ? 'الوصف' : 'Description'}:
                          </span>
                          <p className="text-slate-800 dark:text-white">{selectedFile.description || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                            {language === 'ar' ? 'تاريخ الإنشاء' : 'Created Date'}:
                          </span>
                          <p className="text-slate-800 dark:text-white">{formatDate(Number(selectedFile.createdAt))}</p>
                  </div>
                  <div>
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                            {language === 'ar' ? 'آخر تحديث' : 'Last Updated'}:
                          </span>
                          <p className="text-slate-800 dark:text-white">{formatDate(Number(selectedFile.updatedAt))}</p>
                  </div>
                </div>
                </div>
              </div>
                </div>
              )}
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                  {language === 'ar' ? 'المدفوعات' : 'Payments'}
                </h3>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleRefreshPayments}
                    className="flex items-center space-x-2 px-4 py-2 bg-accent text-primary rounded-lg hover:bg-accent-light transition-colors duration-200 shadow-lg hover:shadow-xl"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span className="font-medium">{language === 'ar' ? 'تحديث' : 'Refresh'}</span>
                  </button>
                  <button
                    onClick={handleExportCSV}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors duration-200 shadow-lg hover:shadow-xl"
                  >
                    <Download className="w-4 h-4" />
                    <span className="font-medium">{language === 'ar' ? 'تصدير CSV' : 'Export CSV'}</span>
                  </button>
                <button
                    onClick={handleAddPayment}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors duration-200 shadow-lg hover:shadow-xl"
                >
                  <Plus className="w-4 h-4" />
                  <span className="font-medium">{language === 'ar' ? 'إضافة دفعة' : 'Add Payment'}</span>
                </button>
                </div>
              </div>
              {loadingEntities ? (
                <div className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-slate-500 dark:text-slate-400">
                    {language === 'ar' ? 'جاري تحميل المدفوعات...' : 'Loading payments...'}
                  </p>
                </div>
              ) : payments.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
                    {language === 'ar' ? 'لا توجد مدفوعات' : 'No payments found'}
                  </h3>
                </div>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => (
                    <div key={payment.id} className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
                              style={{
                                background: isPaymentIncome(payment) 
                                  ? 'linear-gradient(135deg, #2E7D32, #1B5E20)' 
                                  : 'linear-gradient(135deg, #D32F2F, #B71C1C)'
                              }}
                            >
                              💰
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-slate-800 dark:text-white">
                                {payment.description || (language === 'ar' ? 'دفعة' : 'Payment')}
                              </h4>
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                {payment.clientName && `${language === 'ar' ? 'العميل' : 'Client'}: ${payment.clientName}`}
                              </p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-slate-600 dark:text-slate-400">
                                {language === 'ar' ? 'تاريخ الإنشاء' : 'Created Date'}:
                              </span>
                              <p className="font-medium text-slate-800 dark:text-white">
                                {formatDate(Number(payment.createdAt))}
                              </p>
                            </div>
                            <div>
                              <span className="text-slate-600 dark:text-slate-400">
                                {language === 'ar' ? 'نوع الدفع' : 'Payment Type'}:
                              </span>
                              <p 
                                className="font-medium"
                                style={{
                                  color: isPaymentIncome(payment) ? '#2E7D32' : '#D32F2F'
                                }}
                              >
                                {getPaymentTypeLabel(payment.paymentTypeId)}
                              </p>
                            </div>
                          </div>
                          
                          <div className="text-sm">
                            <div className="flex items-center space-x-2">
                              <span className="text-slate-600 dark:text-slate-400">
                                {language === 'ar' ? 'النوع' : 'Type'}:
                              </span>
                              <span 
                                className="px-3 py-1 rounded-full text-sm font-medium"
                                style={{
                                  backgroundColor: isPaymentIncome(payment) ? '#E8F5E8' : '#FFEBEE',
                                  color: isPaymentIncome(payment) ? '#2E7D32' : '#D32F2F'
                                }}
                              >
                                {isPaymentIncome(payment)
                                  ? (language === 'ar' ? 'دخل' : 'Income') 
                                  : (language === 'ar' ? 'مصروف' : 'Expense')
                                }
                              </span>
                            </div>
                          </div>
                          
                          {payment.notes && (
                            <div className="mt-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600">
                              <span className="text-sm text-slate-600 dark:text-slate-400">
                                {language === 'ar' ? 'ملاحظات' : 'Notes'}:
                              </span>
                              <p className="text-sm text-slate-800 dark:text-white mt-1">
                                {payment.notes}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right ml-4 flex flex-col items-end space-y-2">
                          <div className="text-right">
                            <p 
                              className="text-2xl font-bold"
                              style={{
                                color: isPaymentIncome(payment) ? '#2E7D32' : '#D32F2F'
                              }}
                            >
                            {payment.currency || 'EGP'} {payment.value?.toLocaleString() || '0'}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {language === 'ar' ? 'المبلغ' : 'Amount'}
                          </p>
                          </div>
                          <button
                            onClick={() => handleDeletePayment(payment.id)}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg transition-colors duration-200 border border-red-200 dark:border-red-800"
                            title={language === 'ar' ? 'حذف الدفعة' : 'Delete Payment'}
                          >
                            <Trash2 className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              {language === 'ar' ? 'حذف' : 'Delete'}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                {language === 'ar' ? 'الأحداث' : 'Events'}
                </h3>
              {loadingEntities ? (
                <div className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-slate-500 dark:text-slate-400">
                    {language === 'ar' ? 'جاري تحميل الأحداث...' : 'Loading events...'}
                  </p>
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
                    {language === 'ar' ? 'لا توجد أحداث' : 'No events found'}
                  </h3>
                </div>
              ) : (
                <div className="space-y-3">
                    {events.map((event) => (
                    <div key={event.id} className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                          <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <span className="text-primary text-lg">📅</span>
                            </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-800 dark:text-white">
                            {event.title || (language === 'ar' ? 'حدث' : 'Event')}
                              </h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {formatDate(Number(event.createdAt))}
                              </p>
                            </div>
                          </div>
                        </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Attachments Tab */}
          {activeTab === 'attachments' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                  {language === 'ar' ? 'المرفقات' : 'Attachments'}
                </h3>
                <button
                  onClick={handleAddAttachment}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>{language === 'ar' ? 'إضافة مرفق' : 'Add Attachment'}</span>
                </button>
              </div>
              {loadingEntities ? (
                <div className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-slate-500 dark:text-slate-400">
                    {language === 'ar' ? 'جاري تحميل المرفقات...' : 'Loading attachments...'}
                  </p>
                </div>
              ) : attachments.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400 mb-2">
                    {language === 'ar' ? 'لا توجد مرفقات' : 'No attachments found'}
                  </h3>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Horizontal Gallery */}
                  <div className="flex space-x-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent">
                    {attachments.map((attachment) => {
                      const isImage = isImageFile(attachment);
                      return (
                        <div
                          key={attachment.id}
                          className="flex-shrink-0 w-48 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 overflow-hidden hover:shadow-lg transition-shadow duration-200 cursor-pointer group"
                          onClick={() => onViewAttachment(attachment)}
                        >
                          {/* Thumbnail */}
                          <div className="relative">
                            {isImage && attachment.attachmentURL ? (
                              <Base64Image
                                base64={attachment.attachmentURL}
                                mimeType={getMimeTypeFromAttachmentType(attachment.type)}
                                alt={attachment.notes || 'Attachment preview'}
                                className="w-full h-32 object-cover"
                              />
                            ) : (
                              <div className="w-full h-32 bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white font-semibold text-4xl">
                                {getFileIconFromFormat(attachment.format)}
                              </div>
                            )}
                            {/* Overlay on hover */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-t-xl flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <div className="w-6 h-6 text-white">👁️</div>
                              </div>
                            </div>
                          </div>
                          {/* File info */}
                          <div className="p-3">
                            <h4 className="font-semibold text-slate-800 dark:text-white truncate text-sm mb-1">
                              {attachment.notes || (isImage ? 'Image' : 'Document')}
                            </h4>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                              {getAttachmentTypeName(attachment.type, language)} • {attachment.format?.toUpperCase()} • {formatDate(Number(attachment.createdAt))}
                            </p>
                            <div className="flex items-center justify-between">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDownloadAttachment(attachment);
                                }}
                                className="flex items-center space-x-1 text-xs text-primary dark:text-primary-light hover:text-primary-dark dark:hover:text-primary transition-colors"
                              >
                                <span>📥</span>
                                <span>{language === 'ar' ? 'تحميل' : 'Download'}</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteAttachment(attachment.id);
                                }}
                                className="flex items-center space-x-1 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                                title={language === 'ar' ? 'حذف المرفق' : 'Delete Attachment'}
                              >
                                <Trash2 className="w-3 h-3" />
                                <span>{language === 'ar' ? 'حذف' : 'Delete'}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Attachment Modal */}
      <AddAttachmentModal
        isOpen={showAddAttachmentModal}
        onClose={handleCloseAddAttachment}
        selectedFile={selectedFile}
        onAddAttachment={onUploadAttachment}
        user={user}
        selectedRole={selectedRole}
        language={language}
        strings={strings}
      />

      {/* Add Payment Modal */}
      <AddPaymentModal
        isOpen={showAddPaymentModal}
        onClose={handleCloseAddPayment}
        selectedFile={selectedFile}
        onAddPayment={handlePaymentAdded}
        user={user}
        selectedRole={selectedRole}
        language={language}
        strings={strings}
      />
    </div>
  );
};

export default FileDetailsModal;